import { Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { catchError, finalize, of } from 'rxjs';

import { ProxyWorkflowStatusType } from '../../../core/models/proxy-workflow-status-type';
import { SortDirectionType } from '../../../core/models/sort-direction';
import { IconName } from '../../../shared/components/icon/icon-data.generated';
import { IconComponent } from '../../../shared/components/icon/icon.component';
import { ProgressSpinnerComponent } from '../../../shared/components/progress-spinner/progress-spinner.component';
import {
  formatDob,
  formatDobNumeric,
  formatLocalDate,
  formatLocalTime,
  numericDateToIso,
  parseUtc,
} from '../../../shared/utils/date-time/date-time.utils';
import { logHttpError } from '../../../shared/utils/logging/http-error-logging.utils';
import { getProxyStatusBadge } from '../../../shared/utils/status-badge/status-badge.utils';
import { ProxyUserRequestDetail } from '../../models/proxy-user-request-detail';
import { RequestDetailPatient } from '../../models/request-detail';
import {
  PatientPortalProxyUserRequestsService,
  ProxyPatientSearchRequest,
} from '../../services/patient-portal-proxy-user-requests.service';
import {
  formatEmailAddress,
  formatPatientAccountStatus,
  formatPatientAddress,
  formatPatientMrn,
  formatPatientPcp,
  formatPatientPhone,
  splitDisplayName,
} from '../../../shared/utils/patient-display/patient-display.utils';
import { maskSsnLastFive } from '../../utils/identifier.utils';
import { PATIENT_SEARCH_PAGE_SIZE } from '../../utils/patient-search.utils';
import { buildProxyRequestProgress } from '../../utils/proxy-request-progress.utils';
import { AdjustSearch, AdjustSearchCriteria } from '../adjust-search/adjust-search';
import { ProxyApproveRequestModal } from '../proxy-approve-request-modal/proxy-approve-request-modal';
import { ProxyDenyRequestModal } from '../proxy-deny-request-modal/proxy-deny-request-modal';

/** One entry in the Authorization Status card. */
interface AuthorizationEvent {
  id: string;
  description: string;
  /** Sub-line: a timestamp for events that happened, guidance for pending ones. */
  detail: string;
  /** Drives the dot treatment: done, waiting on someone, or ended badly. */
  tone: 'done' | 'pending' | 'failed';
}

/** One staff note, rendered in the Activity Log timeline. */
interface NoteEvent {
  id: number;
  body: string;
  authorName: string;
  date: string;
  time: string;
}

/**
 * The payload carries the field and the request genuinely has no value for it —
 * used only where a null is a fact about the request, not a gap in the contract.
 */
const NOT_PROVIDED = 'Not provided';

@Component({
  selector: 'app-proxy-request-detail',
  standalone: true,
  imports: [
    RouterLink,
    IconComponent,
    ProgressSpinnerComponent,
    AdjustSearch,
    ProxyApproveRequestModal,
    ProxyDenyRequestModal,
  ],
  templateUrl: './proxy-request-detail.html',
  styleUrl: './proxy-request-detail.scss',
})
export class ProxyRequestDetail implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly requestsService = inject(PatientPortalProxyUserRequestsService);

  /**
   * Null when the route param isn't a positive integer (`:requestId` happily
   * matches `abc`), which the template renders as a not-found state rather than
   * firing a request for `NaN`.
   */
  readonly requestId = ProxyRequestDetail.parseRequestId(
    this.route.snapshot.paramMap.get('requestId'),
  );

  readonly isLoading = signal(true);
  readonly loadError = signal<string | null>(null);
  readonly notFound = signal(false);
  readonly request = signal<ProxyUserRequestDetail | null>(null);

  readonly errorTitle = computed(() =>
    this.notFound() ? 'Request not found' : 'Unable to load request',
  );
  readonly errorIcon = computed<IconName>(() => (this.notFound() ? 'search' : 'warning'));

  readonly statusBadge = computed(() => {
    const request = this.request();
    return request ? getProxyStatusBadge(request.workflowStatusTypeId) : null;
  });

  readonly headerTitle = computed(() => {
    const name = this.request()?.name?.trim();
    return name ? `Proxy Request — ${name}` : 'Proxy Request';
  });

  /** "Submitted Jul 28, 2026 · Spouse" under the title. */
  readonly submittedSummary = computed(() => {
    const request = this.request();
    if (!request) return '';

    const relationship = request.relationshipToPatient?.trim();
    const submitted = `Submitted ${formatLocalDate(request.createdOnUtc)}`;
    return relationship ? `${submitted} · ${relationship}` : submitted;
  });

  readonly isMinor = computed(() => this.request()?.isMinor === true);

  readonly patientDateOfBirth = computed(() => {
    const dateOfBirth = this.request()?.patientDateOfBirth;
    return dateOfBirth ? formatDob(dateOfBirth) : NOT_PROVIDED;
  });

  readonly patientIdentifierMedicalRecordNumber = computed(
    () => this.request()?.medicalRecordNumber?.trim() ?? '',
  );
  readonly patientSsnLastFive = computed(
    () => this.request()?.patientLastFiveSocialSecurityNumber?.trim() ?? '',
  );
  readonly maskedPatientSsnLastFive = computed(() => maskSsnLastFive(this.patientSsnLastFive()));

  /**
   * The AC2 contact and account fields, each read straight from the payload and
   * falling back to *Not available* when it is null.
   *
   * The fallback says "not available" rather than "not provided" deliberately: a
   * null means this screen cannot read the value, not that the patient has none on
   * file — the patient's account status has no API source at all yet. Telling staff
   * "not provided" would make a claim about the record we can't support.
   *
   * The patient email and proxy phone are what the requester typed on the intake
   * form, so they are details to verify against the candidate records below rather
   * than confirmed identifiers.
   */
  readonly patientEmail = computed(() => formatEmailAddress(this.request()?.patientEmailAddress));
  readonly patientAccountStatus = computed(() =>
    formatPatientAccountStatus(this.request()?.patientAccountStatusTypeId),
  );
  readonly proxyPhoneNumber = computed(() => formatPatientPhone(this.request()?.phoneNumber));
  readonly proxyEmail = computed(() => formatEmailAddress(this.request()?.emailAddress));

  readonly submittedTimestamp = computed(() => {
    const request = this.request();
    return request
      ? `${formatLocalDate(request.createdOnUtc)} · ${formatLocalTime(request.createdOnUtc)}`
      : '';
  });

  /**
   * Patient record matches from `Bolt/Patients/ProxySearch` — auto-searched from
   * the request's own patient details on load, replaced by each adjusted search.
   * The proxy route is separate from the linking-request one because the call is
   * audited against a patientPortalProxyUserRequestId.
   */
  readonly patientSearchResults = signal<RequestDetailPatient[]>([]);
  readonly isSearching = signal(false);
  /**
   * A failed search must not fall through to the "No matching records found"
   * empty state: on this screen "no match" is the reason staff deny a request,
   * so a dropped call reading as an answer would drive a wrong decision.
   */
  readonly searchError = signal<string | null>(null);
  readonly selectedSearchResultIndex = signal<number | null>(null);
  readonly adjustSearchOpen = signal(false);
  /**
   * What staff last typed into the Adjust Search panel, so re-opening it restores
   * their edits. Set on cancel too — a cancel discards the *search*, not the
   * typing — which is why the results summary keys off `hasAdjustedSearch`
   * instead of this.
   */
  readonly savedAdjustCriteria = signal<AdjustSearchCriteria | null>(null);
  /** True only once an adjusted search actually ran and replaced the results. */
  private readonly hasAdjustedSearch = signal(false);
  /** False until the auto-search resolves, so the empty state waits for it. */
  private readonly hasSearched = signal(false);

  /** The patient name arrives as one string; split it for the search criteria. */
  readonly adjustFirstName = computed(
    () => splitDisplayName(this.request()?.patientName ?? '').firstName,
  );
  readonly adjustLastName = computed(
    () => splitDisplayName(this.request()?.patientName ?? '').lastName,
  );

  readonly patientSearchSummary = computed(() => {
    if (!this.hasSearched() || this.searchError()) {
      return '';
    }

    const count = this.patientSearchResults().length;
    const prefix = this.hasAdjustedSearch() ? 'Adjusted search' : 'Auto-searched on submission';
    return `${prefix} · ${count} result${count === 1 ? '' : 's'}`;
  });

  readonly progressSteps = computed(() => {
    const request = this.request();
    return request ? buildProxyRequestProgress(request.workflowStatusTypeId, request.isMinor) : [];
  });

  readonly authorizationEvents = computed<AuthorizationEvent[]>(() => {
    const request = this.request();
    if (!request) return [];

    const events: AuthorizationEvent[] = [
      {
        id: 'submitted',
        description: `Request submitted for ${request.patientName} by ${request.name}`,
        detail: this.submittedTimestamp(),
        tone: 'done',
      },
    ];

    if (request.proxySignature?.trim() && request.dateOfSignature) {
      events.push({
        id: 'signed',
        description: `Authorization signed by ${request.proxySignature.trim()}`,
        detail: formatDob(request.dateOfSignature),
        tone: 'done',
      });
    }

    if (request.isMinor) {
      events.push({
        id: 'minor-bypass',
        description: 'Email authorization bypassed — patient is a minor',
        detail: 'Minors are verified through their guardian',
        tone: 'done',
      });
    }

    const currentState = this.buildCurrentStateEvent(request);
    if (currentState) {
      events.push(currentState);
    }

    return events;
  });

  /** Newest first — the audit trail reads in reverse chronological order (AC8). */
  readonly notes = computed<NoteEvent[]>(() =>
    [...(this.request()?.notes ?? [])]
      .sort(
        (left, right) =>
          parseUtc(right.createdOnUtc).getTime() - parseUtc(left.createdOnUtc).getTime(),
      )
      .map((note) => ({
        id: note.patientPortalProxyUserRequestNoteId,
        body: note.note,
        authorName: note.createdByDisplayName?.trim() || 'Staff',
        date: formatLocalDate(note.createdOnUtc),
        time: formatLocalTime(note.createdOnUtc),
      })),
  );

  /** Both decisions are confirmed in a modal; null means none is open. */
  readonly activeModal = signal<'deny-request' | 'approve-request' | null>(null);

  /** The record staff picked in the search results — the Approve body's patientId. */
  readonly selectedPatient = computed<RequestDetailPatient | null>(() => {
    const index = this.selectedSearchResultIndex();
    return index === null ? null : (this.patientSearchResults()[index] ?? null);
  });

  /**
   * Approved and denied requests are terminal for this screen. Defence in depth
   * rather than a live case: the admin API's get-by-id scopes to the active
   * queue (Pending / InReview / NeedsInfo), so a settled request 404s here long
   * before these actions could render. See the AC7 note in the PR description.
   */
  readonly isDecided = computed(() => {
    const status = this.request()?.workflowStatusTypeId;
    return status === ProxyWorkflowStatusType.Approved || status === ProxyWorkflowStatusType.Denied;
  });

  /**
   * Statuses a staff member may approve from. InReview is the settled staff-review
   * state; Pending and NeedsInfo are included because the authorization email
   * cannot be sent from here, so either would otherwise be stuck with no way
   * forward. Approved and Denied are already decided.
   */
  readonly isApprovableStatus = computed(() => {
    const status = this.request()?.workflowStatusTypeId;
    return (
      status === ProxyWorkflowStatusType.InReview ||
      status === ProxyWorkflowStatusType.Pending ||
      status === ProxyWorkflowStatusType.NeedsInfo
    );
  });

  /** Approve also needs a patient to link to, so it waits on a selected record. */
  readonly canApprove = computed(
    () => this.isApprovableStatus() && this.selectedPatient() !== null,
  );

  /** Why Approve is unavailable, or null when it is available. */
  readonly approveBlockedReason = computed<string | null>(() => {
    if (this.isDecided()) {
      return 'This request has already been decided — no further action can be taken.';
    }
    if (!this.isApprovableStatus()) {
      return 'This request is still waiting on patient confirmation. It can be approved once it reaches staff review.';
    }
    if (this.selectedPatient() === null) {
      return 'Select a patient record above to approve this request.';
    }
    return null;
  });

  /**
   * AC5: the email authorization action belongs to the two adult states that are
   * waiting on the patient — Pending (never sent) and NeedsInfo (sent, awaiting
   * confirmation). AC3: a minor is verified through their guardian, so the action
   * is absent entirely rather than disabled.
   */
  readonly showEmailAuthorizationAction = computed(() => {
    const status = this.request()?.workflowStatusTypeId;
    return (
      !this.isMinor() &&
      (status === ProxyWorkflowStatusType.Pending || status === ProxyWorkflowStatusType.NeedsInfo)
    );
  });

  /** Pending has never had an email sent, so the first one reads "Send". */
  readonly emailAuthorizationLabel = computed(() =>
    this.request()?.workflowStatusTypeId === ProxyWorkflowStatusType.Pending
      ? 'Send Authorization Email'
      : 'Resend Authorization Email',
  );

  ngOnInit(): void {
    if (this.requestId === null) {
      this.isLoading.set(false);
      this.notFound.set(true);
      this.loadError.set(
        'This request may have been removed, or the link you followed is no longer valid.',
      );
      return;
    }

    this.requestsService
      .getRequestById(this.requestId)
      .pipe(
        catchError((error: { status?: number }) => {
          const notFound = error?.status === 404;
          this.notFound.set(notFound);
          this.loadError.set(
            notFound
              ? 'This request may have been removed, or the link you followed is no longer valid.'
              : 'Something went wrong while loading this request. Please try again.',
          );
          this.isLoading.set(false);
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((request) => {
        if (!request) {
          return;
        }

        this.request.set(request);
        this.loadError.set(null);
        this.notFound.set(false);
        this.isLoading.set(false);

        // The detail payload already carries the patient matches the API found
        // for this request. Showing those is one fewer PHI-audited call than
        // repeating the same search from here. Only a payload with no matches
        // falls through to the client-side search, so this can never leave staff
        // looking at fewer records than the always-search behavior did.
        if (this.applyMatchesFromPayload(request)) {
          return;
        }

        this.runPatientSearch({
          firstName: this.adjustFirstName() || null,
          lastName: this.adjustLastName() || null,
          dateOfBirth: request.patientDateOfBirth,
          lastFiveSocialSecurityNumber: request.patientLastFiveSocialSecurityNumber?.trim() || null,
          medicalRecordNumber: request.medicalRecordNumber?.trim() || null,
        });
      });
  }

  selectSearchResult(resultIndex: number): void {
    this.selectedSearchResultIndex.set(resultIndex);
  }

  openAdjustSearch(): void {
    this.adjustSearchOpen.set(true);
  }

  closeAdjustSearch(): void {
    this.adjustSearchOpen.set(false);
  }

  /**
   * Keeps the typed criteria so re-opening the panel restores them, but leaves
   * the results — and their summary label — untouched: nothing was searched.
   */
  onAdjustSearchCancelled(criteria: AdjustSearchCriteria): void {
    this.savedAdjustCriteria.set(criteria);
    this.closeAdjustSearch();
  }

  /**
   * Staff ran an adjusted search. `criteria.phoneNumber` is deliberately dropped:
   * ProxySearch has no phone field, which is why the panel hides that box here.
   */
  handleSearchRun(criteria: AdjustSearchCriteria): void {
    this.savedAdjustCriteria.set(criteria);
    this.hasAdjustedSearch.set(true);
    this.adjustSearchOpen.set(false);
    this.runPatientSearch({
      firstName: criteria.firstName,
      lastName: criteria.lastName,
      dateOfBirth: numericDateToIso(criteria.dateOfBirth),
      lastFiveSocialSecurityNumber: criteria.lastFiveSsn,
      medicalRecordNumber: criteria.mrn,
    });
  }

  openDenyModal(): void {
    this.activeModal.set('deny-request');
  }

  closeModal(): void {
    this.activeModal.set(null);
  }

  /**
   * Opens the Approve confirmation. The write itself belongs to
   * `ProxyApproveRequestModal` — the first search hit is preselected, so nothing
   * on this screen may grant access without the record being confirmed first.
   * The guard is re-checked here so the modal can never open for a request that
   * isn't approvable, however the click arrived.
   */
  openApproveModal(): void {
    if (this.requestId === null || this.request() === null || !this.canApprove()) {
      return;
    }

    this.activeModal.set('approve-request');
  }

  onRequestApproved(): void {
    this.closeModal();
    this.returnToQueue();
  }

  onRequestDenied(): void {
    this.closeModal();
    this.returnToQueue();
  }

  private returnToQueue(): void {
    this.router.navigate(['/requests/proxy-verifications']);
  }

  getResultDateOfBirth(patient: RequestDetailPatient): string {
    return formatDobNumeric(patient.dateOfBirth);
  }

  getResultAddress(patient: RequestDetailPatient): string {
    return formatPatientAddress(patient.address);
  }

  getResultPhoneNumber(patient: RequestDetailPatient): string {
    return formatPatientPhone(patient.phoneNumber);
  }

  getResultMedicalRecordNumber(patient: RequestDetailPatient): string {
    return formatPatientMrn(patient.medicalRecordNumber);
  }

  getResultPrimaryCareProvider(patient: RequestDetailPatient): string {
    return formatPatientPcp(patient.primaryCareProvider);
  }

  /**
   * Seeds the search results from the `patientMatches` the detail response
   * carried. They widen to `RequestDetailPatient` here on purpose: the adjusted
   * ProxySearch returns records without `isPartialMatch`, so the results signal
   * holds only what both sources can supply.
   *
   * Returns true when it carried some — the search is settled and nothing more
   * needs to run — and false when it carried none, leaving the screen to run
   * the ProxySearch itself.
   */
  private applyMatchesFromPayload(request: ProxyUserRequestDetail): boolean {
    const matches = request.patientMatches ?? [];
    if (matches.length === 0) {
      return false;
    }

    this.patientSearchResults.set(matches);
    this.selectedSearchResultIndex.set(0);
    this.searchError.set(null);
    this.hasSearched.set(true);
    return true;
  }

  /** Posts the proxy patient search and swaps in its results. */
  private runPatientSearch(
    criteria: Omit<
      ProxyPatientSearchRequest,
      'patientPortalProxyUserRequestId' | 'sortDirection' | 'sortColumn' | 'pageNumber' | 'pageSize'
    >,
  ): void {
    if (this.requestId === null) {
      return;
    }

    const searchRequest: ProxyPatientSearchRequest = {
      patientPortalProxyUserRequestId: this.requestId,
      ...criteria,
      sortDirection: SortDirectionType.Ascending,
      sortColumn: 'DisplayName',
      pageNumber: 1,
      pageSize: PATIENT_SEARCH_PAGE_SIZE,
    };

    this.isSearching.set(true);
    this.searchError.set(null);

    this.requestsService
      .searchPatients(searchRequest)
      .pipe(
        finalize(() => {
          this.isSearching.set(false);
          this.hasSearched.set(true);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (patients) => {
          this.patientSearchResults.set(patients);
          this.selectedSearchResultIndex.set(patients.length > 0 ? 0 : null);
        },
        error: (error) => {
          logHttpError('Failed to run proxy patient search', error);
          this.patientSearchResults.set([]);
          this.selectedSearchResultIndex.set(null);
          this.searchError.set(
            'The patient record search could not be completed. This is not a "no match" result — try the search again before deciding this request.',
          );
        },
      });
  }

  /**
   * The tail of the Authorization Status list: what the request is waiting on now.
   * Pending contributes nothing — the list ends with the submission itself.
   */
  private buildCurrentStateEvent(request: ProxyUserRequestDetail): AuthorizationEvent | null {
    switch (request.workflowStatusTypeId) {
      case ProxyWorkflowStatusType.Pending:
        return null;
      case ProxyWorkflowStatusType.NeedsInfo:
        return {
          id: 'state',
          description: 'Awaiting patient confirmation',
          detail: 'The authorization email has been sent and is waiting on the patient',
          tone: 'pending',
        };
      case ProxyWorkflowStatusType.InReview:
        return {
          id: 'state',
          description: 'Awaiting staff review and approval',
          detail: 'Pending — a staff member needs to approve or deny this request',
          tone: 'pending',
        };
      case ProxyWorkflowStatusType.Approved:
        return {
          id: 'state',
          description: 'Proxy access is active',
          detail: 'The proxy can view this patient’s records in the portal',
          tone: 'done',
        };
      case ProxyWorkflowStatusType.Denied:
        return {
          id: 'state',
          description: 'Request denied',
          detail: 'The proxy was not granted access to this patient’s records',
          tone: 'failed',
        };
      default:
        return {
          id: 'state',
          description: 'Status not recognized',
          detail: 'This request carries a workflow status this screen does not name yet',
          tone: 'pending',
        };
    }
  }

  private static parseRequestId(value: string | null): number | null {
    if (value === null || value.trim() === '') {
      return null;
    }

    const parsed = Number(value);
    return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
  }
}
